import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Brain,
  CheckCircle,
  ChevronRight,
  Clock,
  Code,
  Rocket,
  Settings,
} from 'lucide-react';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const learningPaths = [
    {
      title: 'Programming Languages',
      description: 'Master JavaScript, TypeScript, Python with practical examples and interview questions',
      icon: Code,
      link: '/languages',
      items: ['JavaScript Fundamentals', 'TypeScript Basics', 'Python for Interviews']
    },
    {
      title: 'Practice & Problems',
      description: 'DSA problems, Machine Coding, LLD, HLD, and interview challenges',
      icon: Brain,
      link: '/practice',
      items: ['DSA Practice', 'Machine Coding', 'Low-Level Design', 'High-Level System Design']
    },
    {
      title: 'System Design',
      description: 'Understand scalability, architecture patterns, and distributed systems',
      icon: Settings,
      link: '/system-design',
      items: ['Scalability Fundamentals', 'Architecture Patterns', 'Case Studies']
    }
  ];

  const recommendedSteps = [
    { level: 'Beginner', title: 'JavaScript Operators', description: 'Strengthen the language fundamentals behind everyday coding problems.', link: '/languages/javascript/operators' },
    { level: 'Intermediate', title: 'DSA Pattern Practice', description: 'Build consistency with curated problems organized around interview patterns.', link: '/practice/dsa' },
    { level: 'Advanced', title: 'CAP Theorem', description: 'Learn the consistency and availability trade-off behind distributed systems.', link: '/system-design/cap-theorem' },
  ];

  const featuredContent = [
    {
      title: 'JavaScript Operators',
      description: 'Understand comparisons, logical operators, and the rules behind everyday JavaScript expressions.',
      category: 'Programming Languages',
      readTime: '10 min read',
      difficulty: 'Beginner',
      link: '/languages/javascript/operators'
    },
    {
      title: 'CAP Theorem',
      description: 'The fundamental tradeoff of distributed systems — Consistency vs Availability during network partitions',
      category: 'System Design',
      readTime: '12 min read',
      difficulty: 'Intermediate',
      link: '/system-design/cap-theorem'
    },
    {
      title: 'DSA Pattern Practice',
      description: 'Build consistency with curated problem sets organized around the patterns that matter in interviews.',
      category: 'Practice',
      readTime: 'Practice set',
      difficulty: 'Intermediate',
      link: '/practice/dsa'
    }
  ];

  return (
    <div className="dashboard-page dashboard-palette--recallo">
      <div className="dashboard-shell">
        <section className="dashboard-hero" aria-labelledby="dashboard-heading">
          <div className="dashboard-hero__glow dashboard-hero__glow--right" aria-hidden="true" />
          <div className="dashboard-hero__glow dashboard-hero__glow--left" aria-hidden="true" />

          <div className="dashboard-hero__content">
            <div className="dashboard-eyebrow">
              <Rocket aria-hidden="true" />
              <span>Master Technical Interviews</span>
            </div>

            <h1 id="dashboard-heading" className="dashboard-hero__title">
              Level Up Your
              <span>Engineering Skills</span>
            </h1>

            <p className="dashboard-hero__description">
              Learn by doing — master data structures, system design, and full-stack patterns through hands-on practice, live coding environments, and 100+ in-depth articles.
            </p>

            <div className="dashboard-actions">
              <Link to="/practice" className="dashboard-button dashboard-button--primary">
                Start Practicing
                <ArrowRight aria-hidden="true" />
              </Link>
              <Link to="/system-design" className="dashboard-button dashboard-button--secondary">
                Explore System Design
              </Link>
            </div>

            <div className="dashboard-stats" aria-label="Platform statistics">
              {[
                ['250+', 'DSA Problems'],
                ['100+', 'In-Depth Articles'],
                ['5+', 'Coding Playgrounds'],
                ['4+', 'Languages'],
              ].map(([value, label]) => (
                <div className="dashboard-stat" key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="dashboard-section" aria-labelledby="learning-paths-heading">
          <header className="dashboard-section__header dashboard-section__header--centered">
            <h2 id="learning-paths-heading">Choose Your Learning Journey</h2>
            <p>Structured, industry-tested pathways designed by engineers from top tech companies</p>
          </header>

          <div className="learning-paths-grid">
            {learningPaths.map((path) => (
              <Link key={path.title} to={path.link} className="dashboard-card learning-path-card">
                <div className="learning-path-card__top">
                  <div className="learning-path-card__icon" aria-hidden="true">
                    <path.icon />
                  </div>
                  <div>
                    <h3>{path.title}</h3>
                    <p>{path.description}</p>
                  </div>
                </div>

                <div className="learning-path-card__items">
                  {path.items.map((item) => (
                    <div key={item}>
                      <CheckCircle aria-hidden="true" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="dashboard-card__link">
                  <span>Explore</span>
                  <ArrowRight aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="dashboard-section dashboard-start-section" aria-labelledby="recommended-steps-heading">
          <header className="dashboard-section__header">
            <h2 id="recommended-steps-heading">Recommended next steps</h2>
            <p>Pick one focused topic and make progress immediately. Your path can change as your preparation develops.</p>
          </header>

          <div className="dashboard-start-grid">
            {recommendedSteps.map((step) => (
              <Link key={step.title} to={step.link} className="dashboard-card dashboard-start-card">
                <div>
                  <span className="dashboard-start-card__level">{step.level}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <span className="dashboard-start-card__action">Open topic <ArrowRight aria-hidden="true" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="dashboard-section" aria-labelledby="featured-content-heading">
          <header className="dashboard-section__header dashboard-section__header--split">
            <div>
              <h2 id="featured-content-heading">Featured Content</h2>
              <p>Handpicked topics to accelerate your interview preparation</p>
            </div>
            <Link to="/practice" className="dashboard-text-link">
              Explore more <ChevronRight aria-hidden="true" />
            </Link>
          </header>

          <div className="featured-content-grid">
            {featuredContent.map((content) => (
              <Link key={content.title} to={content.link} className="dashboard-card featured-card">
                <div className="featured-card__meta">
                  <span className="dashboard-chip">{content.category}</span>
                  <span className="featured-card__read-time">
                    <Clock aria-hidden="true" />
                    {content.readTime}
                  </span>
                </div>

                <h3>{content.title}</h3>
                <p>{content.description}</p>

                <div className="featured-card__footer">
                  <span className={`difficulty-chip difficulty-chip--${content.difficulty.toLowerCase()}`}>
                    {content.difficulty}
                  </span>
                  <ChevronRight aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="dashboard-quick-start" aria-labelledby="quick-start-heading">
          <div>
            <h2 id="quick-start-heading">Ready to start?</h2>
            <p>
              Pick a language to build fundamentals, jump into system design concepts, or start solving DSA problems — whatever fits where you are right now.
            </p>
          </div>
          <div className="dashboard-quick-start__actions">
            <Link to="/languages" className="dashboard-button dashboard-button--primary dashboard-button--compact">
              Languages <ArrowRight aria-hidden="true" />
            </Link>
            <Link to="/practice" className="dashboard-button dashboard-button--secondary dashboard-button--compact">
              Practice <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
